/**
 * Shared DOM-clone export engine for reports/dashboards that need a
 * downloadable artifact which looks pixel-identical to what's on screen.
 *
 * Clones a live report root, inlines every page stylesheet + `<img>` as a
 * `data:` URL so the result is fully self-contained, then offers two output
 * modes built from the exact same standalone HTML:
 *   - HTML: downloads a standalone .html file (opens offline, any browser).
 *   - PDF: writes the same HTML into a hidden iframe and triggers the
 *     browser's native print dialog, so "Save as PDF" reproduces the live
 *     page exactly — real browser rendering, so no compatibility issues
 *     with modern CSS (unlike html2canvas, which can't parse Tailwind v4's
 *     oklch() colors and crashes instead of rendering). The iframe's
 *     document title is set to the desired filename since most browsers
 *     pre-fill the print dialog's "Save As" name from it — the closest
 *     thing to a controlled filename without a forced silent save (browsers
 *     don't allow JS to save a file to disk without user confirmation).
 *
 * Generalizes the approach first built for the monthly report HTML export.
 */

export interface DomExportResult {
  ok: boolean;
  fileName?: string;
  error?: string;
}

export interface DomExportOpts {
  /** CSS selector for the report root element to clone (must be currently mounted). */
  rootSelector: string;
  /** Document `<title>` / PDF print-job title. */
  title: string;
  /** Base filename without extension, e.g. "monthly-soc-report-acme-2026-07". */
  fileNameBase: string;
  /** Elements to strip from the clone before export. Defaults to ['.dr-no-print']. */
  stripSelectors?: string[];
  /** Optional banner HTML inserted above the cloned content. HTML export only — stripped for print/PDF since it's not part of the on-screen report itself. */
  bannerHtml?: string;
  /** Extra CSS text appended after the collected document stylesheet bundle. */
  extraCss?: string;
  /** Error message used when `rootSelector` doesn't resolve to a mounted element. */
  notMountedError?: string;
}

async function buildStandaloneHtml(
  opts: DomExportOpts,
  { includeBanner }: { includeBanner: boolean },
): Promise<string> {
  const root = document.querySelector<HTMLElement>(opts.rootSelector);
  if (!root) {
    throw new Error(opts.notMountedError ?? 'Report content not visible - load it first.');
  }
  const clone = root.cloneNode(true) as HTMLElement;

  const stripSelectors = opts.stripSelectors ?? ['.dr-no-print'];
  for (const sel of stripSelectors) {
    clone.querySelectorAll(sel).forEach((el) => el.remove());
  }

  // Recharts' ResponsiveContainer infers width from a flexbox parent; in a
  // detached/cloned document that can collapse to 0. Give it a floor.
  clone.querySelectorAll<HTMLElement>('.recharts-responsive-container').forEach((el) => {
    if (!el.style.width) el.style.width = '100%';
    if (!el.style.minWidth) el.style.minWidth = '320px';
  });

  await inlineImages(clone);
  const documentCss = await collectDocumentCss();
  const banner = includeBanner ? (opts.bannerHtml ?? '') : '';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="generator" content="Kloudsecure SOC Dashboard">
<title>${escapeHtml(opts.title)}</title>
<style>
/* === Dashboard CSS bundle (inlined for offline/print rendering) === */
${documentCss}
/* === Export wrapper === */
html, body { margin: 0; padding: 0; background: #ffffff; font-family: "Segoe UI", system-ui, -apple-system, sans-serif; color: #1f2937; }
body { padding: 24px; }
/* Force background colors/gradients to survive print / Save-as-PDF instead
   of being stripped to a blank white page by default browser behavior. */
* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
@media print {
  html, body { padding: 0; }
}
${opts.extraCss ?? ''}
</style>
</head>
<body>
${banner}
${clone.outerHTML}
</body>
</html>`;
}

/** Download a standalone HTML snapshot of the live report. */
export async function exportDomAsHtml(opts: DomExportOpts): Promise<DomExportResult> {
  try {
    const html = await buildStandaloneHtml(opts, { includeBanner: true });
    const fileName = `${opts.fileNameBase}.html`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Defer revoke — Safari needs the URL alive briefly after click().
    setTimeout(() => URL.revokeObjectURL(url), 4_000);
    return { ok: true, fileName };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Failed to build HTML export.' };
  }
}

/**
 * Trigger the browser's native print dialog against a pixel-faithful clone
 * of the live report, so "Save as PDF" matches the on-screen view exactly.
 * Printing happens via `iframe.contentWindow.print()`, but most browsers'
 * "Save As" dialog suggests a filename from the TOP-LEVEL page's title, not
 * the iframe's own — so the real page title is temporarily swapped to
 * `fileNameBase` around the print call and restored afterward.
 */
export async function exportDomAsPdf(opts: DomExportOpts): Promise<DomExportResult> {
  let iframe: HTMLIFrameElement | null = null;
  const originalTitle = document.title;
  try {
    const html = await buildStandaloneHtml({ ...opts, title: opts.fileNameBase }, { includeBanner: false });

    iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) throw new Error('Could not prepare the print document.');
    await new Promise<void>((resolve) => {
      doc.open();
      doc.write(html);
      doc.close();
      // Images/fonts inside the iframe may still be loading after doc.write
      // returns — give layout a moment to settle before printing.
      setTimeout(resolve, 400);
    });

    const win = iframe.contentWindow;
    if (!win) throw new Error('Could not open the print window.');
    document.title = opts.fileNameBase;
    win.focus();
    win.print();

    // There's no reliable cross-browser "print dialog closed" event, so
    // restore the real title and remove the iframe on a generous timeout —
    // the title only needs to hold while the dialog is actually open, and
    // removing the iframe too early can silently cancel printing.
    const frameToRemove = iframe;
    setTimeout(() => {
      document.title = originalTitle;
      frameToRemove.remove();
    }, 60_000);

    return { ok: true, fileName: `${opts.fileNameBase}.pdf` };
  } catch (err) {
    iframe?.remove();
    document.title = originalTitle;
    return { ok: false, error: err instanceof Error ? err.message : 'Failed to build PDF export.' };
  }
}

/**
 * Serialise every reachable stylesheet on the page into a single CSS string.
 * Same-origin sheets expose `cssRules`; cross-origin sheets throw on access
 * (CORS) so we fall back to fetching the href and embedding the raw text.
 * Failures are skipped silently — a missing rule is better than a failed
 * export.
 */
async function collectDocumentCss(): Promise<string> {
  const parts: string[] = [];
  const sheets = Array.from(document.styleSheets);
  for (const sheet of sheets) {
    try {
      const rules = sheet.cssRules;
      if (rules) {
        let buf = '';
        for (let i = 0; i < rules.length; i++) {
          buf += rules[i]?.cssText ?? '';
          buf += '\n';
        }
        if (buf.length > 0) parts.push(buf);
        continue;
      }
    } catch {
      // CORS-protected sheet — try fetching the href below.
    }
    if (sheet.href) {
      try {
        const res = await fetch(sheet.href, { credentials: 'same-origin' });
        if (res.ok) {
          const text = await res.text();
          if (text.length > 0) parts.push(text);
        }
      } catch {
        // ignore — best effort only
      }
    }
  }
  return parts.join('\n');
}

/**
 * Replace every `<img src="…">` in `root` with a `src="data:…;base64,…"`.
 * Done in parallel; if a fetch fails the original src is left in place.
 */
async function inlineImages(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute('src');
      if (!src || src.startsWith('data:')) return;
      try {
        const res = await fetch(src, { credentials: 'same-origin' });
        if (!res.ok) return;
        const blob = await res.blob();
        const dataUrl = await blobToDataUrl(blob);
        img.setAttribute('src', dataUrl);
      } catch {
        // leave original src — file may still render if user is online.
      }
    }),
  );
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}

export function slugify(s: string): string {
  return (
    (s || 'export')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'export'
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
